import React, { useEffect, useRef, useState } from 'react';
import { motion, useSpring, useMotionValue } from 'framer-motion';

const CustomCursor = () => {
  const [hoverTarget, setHoverTarget] = useState(null);
  const [hidden, setHidden] = useState(true);
  const [isTouch, setIsTouch] = useState(false);
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const springConfig = { damping: 28, stiffness: 320, mass: 0.5 };
  const sx = useSpring(x, springConfig);
  const sy = useSpring(y, springConfig);
  const ringRef = useRef(null);

  useEffect(() => {
    const isTouchDevice =
      'ontouchstart' in window ||
      (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0);
    if (isTouchDevice) {
      setIsTouch(true);
      return;
    }

    const move = (e) => {
      setHidden(false);
      x.set(e.clientX);
      y.set(e.clientY);
    };
    const leave = () => setHidden(true);
    const enter = () => setHidden(false);

    const onOver = (e) => {
      const el = e.target.closest(
        'a, button, [role="button"], input, textarea, select, label, [data-cursor="hover"]'
      );
      if (el) {
        const label = el.getAttribute('data-cursor-label');
        setHoverTarget(label || 'hover');
      }
    };
    const onOut = (e) => {
      const el = e.target.closest(
        'a, button, [role="button"], input, textarea, select, label, [data-cursor="hover"]'
      );
      if (el) setHoverTarget(null);
    };

    window.addEventListener('mousemove', move);
    window.addEventListener('mouseleave', leave);
    window.addEventListener('mouseenter', enter);
    document.addEventListener('mouseover', onOver);
    document.addEventListener('mouseout', onOut);
    document.body.classList.add('custom-cursor-active');

    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseleave', leave);
      window.removeEventListener('mouseenter', enter);
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseout', onOut);
      document.body.classList.remove('custom-cursor-active');
    };
  }, [x, y]);

  if (isTouch) return null;

  const isHovering = Boolean(hoverTarget);
  const label = hoverTarget && hoverTarget !== 'hover' ? hoverTarget : null;

  return (
    <>
      {/* Outer ring */}
      <motion.div
        ref={ringRef}
        aria-hidden="true"
        className="pointer-events-none fixed top-0 left-0 z-[9999] rounded-full mix-blend-difference"
        style={{
          x: sx,
          y: sy,
          translateX: '-50%',
          translateY: '-50%',
        }}
        animate={{
          width: isHovering ? 88 : 36,
          height: isHovering ? 88 : 36,
          backgroundColor: isHovering ? '#AA94FF' : 'rgba(244, 244, 243, 0.0)',
          borderColor: isHovering ? '#AA94FF' : '#F4F4F3',
          opacity: hidden ? 0 : 1,
        }}
        transition={{ type: 'spring', damping: 22, stiffness: 260 }}
      >
        <div
          className="w-full h-full rounded-full border-2 flex items-center justify-center text-[10px] font-bold uppercase tracking-[0.15em] text-[#F4F4F3]"
          style={{ borderColor: 'inherit' }}
        >
          {label}
        </div>
      </motion.div>

      {/* Dot */}
      <motion.div
        aria-hidden="true"
        className="pointer-events-none fixed top-0 left-0 z-[9999] w-2 h-2 rounded-full bg-[#AA94FF] mix-blend-difference"
        style={{
          x,
          y,
          translateX: '-50%',
          translateY: '-50%',
        }}
        animate={{ opacity: hidden ? 0 : isHovering ? 0 : 1, scale: isHovering ? 0 : 1 }}
        transition={{ duration: 0.15 }}
      />
    </>
  );
};

export default CustomCursor;
